import React, { useEffect, useState, useContext, useCallback } from "react";
import { AuthContext } from "../context/AuthContext";
import {
  fetchNotifications,
  markMessagesAsSeen,
  updateRideRequest,
  fetchDriverPendingRequests,
  SERVER_URL 
} from "../utils/api";
import "../css/notifications.css";

// ✅ IMPORT YOUR NEW CLEAN COMPONENT HERE!
import NotificationRequestsTab from "./RequestsTab";

export default function NotificationList({ onNotificationClick, onClose }) {
  const { user, token } = useContext(AuthContext);
  const userId = user?._id || user?.id;

  const [notifications, setNotifications] = useState([]);
  const [requests, setRequests] = useState([]);
  const [activeTab, setActiveTab] = useState("notifications");
  const [loading, setLoading] = useState(false);

  const formatTime = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const loadNotifications = useCallback(async () => {
    if (!userId || !token) return;
    try {
      const all = await fetchNotifications(userId, token);
      const filtered = (all || []).filter((notif) => !notif?.seen);
      setNotifications(filtered);
    } catch (err) {
      console.error("Notifications fetch error:", err);
    }
  }, [userId, token]);

  const loadRequests = useCallback(async () => {
    if (!userId || !token) return;
    try {
      const pending = await fetchDriverPendingRequests(userId, token);
      setRequests(pending || []);
    } catch (err) {
      console.error("Requests fetch error:", err);
    }
  }, [userId, token]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadNotifications(), loadRequests()]).finally(() => setLoading(false));

    const interval = setInterval(() => {
      loadNotifications();
      loadRequests();
    }, 10000);

    return () => clearInterval(interval);
  }, [loadNotifications, loadRequests]);

  const handleNotificationClick = async (notification) => {
    try {
      await markMessagesAsSeen([notification._id], token);
      setNotifications((prev) => prev.filter((n) => n._id !== notification._id));
      onNotificationClick?.(notification);
      onClose?.();
    } catch (err) {
      console.error("Notification click error:", err);
    }
  };

  const handleRequestAction = async (rideId, requestId, action) => {
    try {
      const { request: updated } = await updateRideRequest(rideId, requestId, action, token);
      if (updated) {
        setRequests((prev) => prev.filter((r) => r._id !== updated._id));
        loadNotifications(); // Reload alerts to show confirmation
      }
    } catch (err) {
      alert(err?.response?.data?.error || "Failed to update request");
    }
  };

  const getAvatar = (userObj) => {
    const raw = userObj?.avatar || userObj?.image;
    if (!raw) return "/default-avatar.png";
    return raw.startsWith("http") ? raw : `${SERVER_URL}${raw}`;
  };

  return (
    <div className="notification-list">
      <header className="notif-header">
        <div className="notif-header-top">
          <h3>Activity</h3>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        
        <div className="tabs">
          <button
            className={activeTab === "notifications" ? "active" : ""}
            onClick={() => setActiveTab("notifications")}
          >
            Alerts
            {notifications.length > 0 && <span className="badge-count">{notifications.length}</span>}
          </button>
          <button
            className={activeTab === "requests" ? "active" : ""}
            onClick={() => setActiveTab("requests")}
          >
            Requests
            {requests.length > 0 && <span className="badge-count">{requests.length}</span>}
          </button>
        </div>
      </header>

      <div className="notif-content-area">
        {loading && notifications.length === 0 && requests.length === 0 && (
          <div className="notif-loading">
             <div className="spinner-small"></div>
             Checking updates...
          </div>
        )}

        {/* 🔔 ALERTS TAB */}
        {activeTab === "notifications" && (
          <div className="notif-body">
            {notifications.length === 0 && !loading && (
              <div className="notif-empty">
                <div className="empty-icon">🔔</div>
                <p>No new alerts</p>
              </div>
            )}
            {notifications.map((notif) => {
              let displayMessage = notif.message || notif.content || "New notification";
              
              if (!notif.message && !notif.content) {
                if (notif.type === "call") displayMessage = "📞 Missed call";
                else if (notif.type === "ride_seat_confirmed") displayMessage = "✅ Approved your seat request";
                else if (notif.type === "ride_seat_rejected") displayMessage = "❌ Declined your seat request";
                else if (notif.type === "ride_cancelled") displayMessage = "🚫 Ride has been cancelled";
              }

              return (
                <div key={notif._id} className="notif-item" onClick={() => handleNotificationClick(notif)}>
                  <img src={getAvatar(notif.from)} alt="avatar" className="notif-avatar" />
                  <div className="notif-details">
                    <div className="notif-top">
                      <strong>{notif.from?.name || "System"}</strong>
                      <span className="notif-time">
                        {formatTime(notif.createdAt || notif.timestamp)}
                      </span>
                    </div>
                    <p className="notif-text">{displayMessage}</p>
                  </div>
                  <div className="notif-indicator"></div>
                </div>
              );
            })}
          </div>
        )}

        {/* 🚗 REQUESTS TAB (Now fully separated!) */}
        {activeTab === "requests" && (
          <div className="notif-body">
            <NotificationRequestsTab 
              requests={requests} 
              loading={loading} 
              handleRequestAction={handleRequestAction} 
              getAvatar={getAvatar} 
            />
          </div>
        )}
      </div>
    </div>
  );
}